import * as React from 'react';
import { cn } from '@/lib/utils';
import { Combobox, ComboboxChip, ComboboxChips, ComboboxChipsInput, ComboboxEmpty, ComboboxItem, ComboboxList, ComboboxPopup, ComboboxValue } from '@/components/ui/combobox';

/**
 * Componente Combobox de múltipla seleção.
 */
export const ComboboxMultiple = React.forwardRef(
  (
    {
      className,
      options = [],
      labelKey = 'label',
      valueKey = 'value',
      placeholder = 'Selecione...',
      loading = false,
      emptyMessage = 'Nenhum resultado encontrado.',
      value = [], // 👈 array de strings/IDs ou objetos
      onValueChange, // 👈 devolve array de strings/IDs
      disabled = false,
      ...props
    },
    ref
  ) => {
    // 🔁 transforma array de IDs -> array de objetos
    const selectedItems = React.useMemo(() => {
      if (!Array.isArray(value)) return [];
      return value
        .map(val => {
          if (typeof val === 'object') return val;
          return options.find(opt => opt?.[valueKey] === val);
        })
        .filter(Boolean);
    }, [value, options, valueKey]);

    // 🔁 transforma array de objetos -> array de IDs
    const handleChange = React.useCallback(
      items => {
        if (!onValueChange) return;
        if (!Array.isArray(items)) {
          onValueChange([]);
          return;
        }
        onValueChange(items.map(item => item?.[valueKey]));
      },
      [onValueChange, valueKey]
    );

    // Helper para converter o item em texto
    const getItemText = React.useCallback(
      item => {
        if (!item) return '';
        if (typeof item === 'string') return item;
        return item?.[labelKey] ?? '';
      },
      [labelKey]
    );

    return (
      <Combobox
        ref={ref}
        items={options}
        multiple
        className={cn('w-full', className)}
        getItemText={getItemText}
        value={selectedItems}
        onValueChange={handleChange}
        equals={(a, b) => a?.[valueKey] === b?.[valueKey]}
        disabled={disabled}
        {...props}
      >
        <ComboboxChips>
          <ComboboxValue>
            {value => (
              <>
                {value?.map(item => (
                  <ComboboxChip
                    key={item.value}
                    aria-label={item.value}
                  >
                    {item.label}
                  </ComboboxChip>
                ))}
                <ComboboxChipsInput
                  placeholder={selectedItems.length > 0 ? undefined : placeholder}
                  aria-label={placeholder}
                />
              </>
            )}
          </ComboboxValue>
        </ComboboxChips>
        <ComboboxPopup>
          <ComboboxEmpty>{loading ? 'Carregando opções...' : emptyMessage}</ComboboxEmpty>
          <ComboboxList>
            {item => (
              <ComboboxItem
                key={item[valueKey]}
                value={item}
              >
                {getItemText(item)}
              </ComboboxItem>
            )}
          </ComboboxList>
        </ComboboxPopup>
      </Combobox>
    );
  }
);

ComboboxMultiple.displayName = 'ComboboxMultiple';
